import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import RadioButtonCheckedRoundedIcon from "@mui/icons-material/RadioButtonCheckedRounded";
import TodayHeroCard from "./components/TodayHeroCard";
import SignalRow from "./components/SignalRow";
import TodayActions from "./components/TodayActions";
import ProgressDashboardCard from "./components/ProgressDashboardCard";
import SprintSnapshotCard from "./components/SprintSnapshotCard";
import MomentumChart from "./components/MomentumChart";

function cleanTodayInsightText(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .replace(/\s*\{["\s\S]*$/g, "")
    .replace(/^"+|"+$/g, "")
    .trim();
}

function buildRecoveryMove(recoveryIntelligence) {
  if (!recoveryIntelligence?.message && !recoveryIntelligence?.micro_action) return null;
  return {
    label: "Today’s reset",
    title: recoveryIntelligence.cta_label || "Restart with one small habit",
    body:
      cleanTodayInsightText(recoveryIntelligence.message) ||
      "Do not restart the whole plan. Do the smallest useful version of one habit now.",
    microAction: cleanTodayInsightText(recoveryIntelligence.micro_action),
    cta: "Start small",
  };
}

function buildOpportunityMove(opportunity) {
  if (!opportunity?.title && !opportunity?.body) return null;
  return {
    label: "TrustBud nudge",
    title: opportunity.title || opportunity.recommended_user_action || "Take the next useful step",
    body: opportunity.body || "TrustBud found one habit that can help today.",
    action: opportunity.recommended_user_action || "Open",
    intent: opportunity.intent || "",
  };
}

function TodayStoryCard({ story }) {
  if (!story?.body) return null;
  return (
    <Box
      sx={{
        borderRadius: "20px",
        border: "1px solid #dbe7f3",
        backgroundColor: "#ffffff",
        p: { xs: 1.6, md: 1.8 },
        boxShadow: "0 12px 26px rgba(15, 35, 59, 0.06)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.9 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            backgroundColor: "#eef6ff",
            color: "#245d8f",
            flexShrink: 0,
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, color: "#536987", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.65 }}>
            Today story
          </Typography>
          <Typography sx={{ color: "#102033", fontWeight: 900, fontSize: "1.05rem", lineHeight: 1.2 }}>
            {story.title || "What happened today"}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ color: "#31445f", fontWeight: 760, fontSize: "0.98rem", lineHeight: 1.45 }}>
        {story.body}
      </Typography>
      {story.events?.length ? (
        <Stack spacing={0.75} sx={{ mt: 1.25 }}>
          {story.events.map((event, index) => (
            <Box
              key={`${event.type || "event"}-${event.created_at || index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "18px minmax(0, 1fr)",
                gap: 0.85,
                borderTop: index ? "1px solid #edf3f9" : "none",
                pt: index ? 0.8 : 0,
              }}
            >
              <RadioButtonCheckedRoundedIcon sx={{ fontSize: 13, color: "#2d8a5f", mt: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "#102033", fontWeight: 850, fontSize: 14, lineHeight: 1.25 }}>
                  {event.title}
                </Typography>
                {event.body ? (
                  <Typography sx={{ color: "#66758b", fontSize: 13, lineHeight: 1.35, mt: 0.1 }}>
                    {event.body}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function TodayInlinePrompt({ label, title, body, cta, onClick, tone = "neutral" }) {
  const accent = tone === "success" ? "#2d8a5f" : "#17345d";
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: "100%",
        textAlign: "left",
        borderRadius: "20px",
        border: `1px solid ${tone === "success" ? "#cce6d7" : "#d8e4f0"}`,
        backgroundColor: "#ffffff",
        px: { xs: 1.45, md: 1.65 },
        py: { xs: 1.35, md: 1.45 },
        cursor: "pointer",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
        gap: 1.2,
        alignItems: "center",
        boxShadow: "0 12px 26px rgba(15, 35, 59, 0.05)",
        transition: "border-color 120ms ease, background-color 120ms ease",
        "&:hover": {
          borderColor: "#bdcad9",
          backgroundColor: "#fbfcfe",
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, color: accent, fontWeight: 850, textTransform: "uppercase", letterSpacing: 0.55 }}>
          {label}
        </Typography>
        <Typography sx={{ mt: 0.2, fontWeight: 760, color: "#102033", lineHeight: 1.25, fontSize: "0.98rem" }}>
          {title}
        </Typography>
        {body ? (
          <Typography variant="body2" sx={{ mt: 0.2, color: "#60738c", lineHeight: 1.38, fontSize: "0.9rem" }}>
            {body}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          justifySelf: { xs: "start", sm: "end" },
          borderRadius: 999,
          backgroundColor: accent,
          color: "#ffffff",
          px: 1.05,
          py: 0.52,
          fontSize: "0.76rem",
          fontWeight: 850,
          whiteSpace: "nowrap",
        }}
      >
        {cta || "Open"}
      </Box>
    </Box>
  );
}

export default function TodayPage({
  model,
  onActionClick,
  onSmallVersion,
  onResetToday,
  savingActionKey = "",
  onSendUpdate,
  onOpenPlan,
  onOpenSprint,
  onOpenConnections,
  onOpenWeeklySummary,
  recoveryIntelligence = null,
  todayOpportunity = null,
  onOpportunityAction,
  onRecoveryAction,
  feedback = "",
  sprintLoading = false,
  sprintError = "",
}) {
  const recoveryMove = buildRecoveryMove(recoveryIntelligence);
  const opportunityMove = buildOpportunityMove(todayOpportunity);

  return (
    <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: { xs: 1.55, lg: 1.8 },
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box>
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: "1.9rem", lg: "2.25rem" }, fontWeight: 900, letterSpacing: 0, color: "#102033", lineHeight: 1.02 }}
          >
            Today
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.45, color: "#60738c", fontSize: "1rem" }}>
            Focus on what matters. Take the next step.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={onOpenPlan}
          sx={{
            textTransform: "none",
            borderRadius: "999px",
            px: 1.65,
            py: 0.85,
            fontWeight: 850,
            color: "#17345d",
            borderColor: "#cbd8e6",
            backgroundColor: "#ffffff",
            boxShadow: "0 10px 22px rgba(15, 35, 59, 0.06)",
            "&:hover": {
              borderColor: "#9fb1c4",
              backgroundColor: "#ffffff",
            },
          }}
        >
          Open Plan
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.35fr) minmax(318px, 0.82fr)" },
          gap: { xs: 1.45, lg: 1.6 },
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <TodayHeroCard
            state={model.state}
            chip={model.copy.chip}
            title={model.copy.title}
            body={model.copy.body}
            partnerContextLine={model.hero.partnerContextLine}
            supportingLine={model.hero.supportingLine}
            ring={model.ring}
            primaryAction={model.hero.primary}
            secondaryAction={model.hero.secondary}
          />
          <SignalRow items={model.signals} />
          <TodayActions
            incomplete={model.actions.incomplete}
            completed={model.actions.completed}
            empty={model.actions.empty}
            emptyTitle={model.actions.emptyTitle}
            emptyKind={model.actions.emptyKind}
            hasActiveSprint={model.sprint?.status === "Active"}
            onActionClick={onActionClick}
            onSmallVersion={onSmallVersion}
            onResetToday={onResetToday}
            savingActionKey={savingActionKey}
            onSendUpdate={onSendUpdate}
            onOpenPlan={onOpenPlan}
            feedback={feedback}
            goalOptions={model.progress.goalOptions}
            selectedGoalId={model.progress.selectedGoalId}
            onSelectGoal={model.progress.onSelectGoal}
          />
          <Stack spacing={1.15} sx={{ mt: 1.15 }}>
            {opportunityMove ? (
              <TodayInlinePrompt
                label={opportunityMove.label}
                title={opportunityMove.title}
                body={opportunityMove.body}
                cta={opportunityMove.action}
                onClick={() => onOpportunityAction?.(todayOpportunity)}
              />
            ) : null}
            {recoveryMove ? (
              <TodayInlinePrompt
                label={recoveryMove.label}
                title={recoveryMove.title}
                body={recoveryMove.microAction || recoveryMove.body}
                cta={recoveryMove.cta}
                onClick={onRecoveryAction}
                tone="success"
              />
            ) : null}
            <TodayStoryCard story={model.story} />
          </Stack>
        </Box>

        <Stack spacing={{ xs: 1.35, lg: 1.25 }} sx={{ minWidth: 0 }}>
          <SprintSnapshotCard
            sprint={model.sprint}
            onOpenSprint={onOpenSprint}
            onOpenConnections={onOpenConnections}
            loading={sprintLoading}
            error={sprintError}
          />
          <ProgressDashboardCard
            actionsCompleted7={model.progress.actionsCompleted7}
            consistencyPercent={model.progress.consistencyPercent}
            helper={model.progress.helper}
            chartData={model.progress.chartData}
            goalOptions={model.progress.goalOptions}
            selectedGoalId={model.progress.selectedGoalId}
            onSelectGoal={model.progress.onSelectGoal}
            onOpenWeeklySummary={onOpenWeeklySummary}
          />
          <Box
            sx={{
              borderRadius: "20px",
              border: "1px solid #dbe7f3",
              backgroundColor: "#ffffff",
              px: { xs: 1.55, lg: 1.7 },
              py: { xs: 1.45, lg: 1.55 },
              boxShadow: "0 12px 26px rgba(15, 35, 59, 0.05)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 0, color: "#15243b", fontSize: "1.05rem" }}>
                  Momentum history
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.2, color: "#8192a8", fontSize: "0.86rem" }}>
                  This line shows how active you were each day across check-ins and completed habits.
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: "#8192a8", fontWeight: 700, ml: 2, fontSize: "0.82rem" }}>
                Last 7 days
              </Typography>
            </Box>
            <MomentumChart data={model.history} />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
